interface HeroProps {
  onCTAClick: () => void;
}

export default function Hero({ onCTAClick }: HeroProps) {
  return (
    <section className="relative min-h-[100vh] flex items-center pt-[60px] overflow-hidden bg-[#FAFBFC]">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[60%] h-full bg-gradient-to-bl from-[#FF6B35]/8 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-[40%] h-[60%] bg-gradient-to-tr from-[#1A1F36]/5 via-transparent to-transparent"></div>
      </div>
      
      {/* Floating accent shapes */}
      <div className="absolute top-[20%] right-[8%] w-3 h-3 bg-[#FF6B35] rounded-full animate-pulse"></div>
      <div className="absolute top-[35%] right-[15%] w-2 h-2 bg-[#FF6B35]/60 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      <div className="absolute top-[25%] right-[22%] w-1.5 h-1.5 bg-[#FF6B35]/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
      
      {/* Geometric accent lines */}
      <div className="absolute top-[15%] right-[5%] w-32 h-[1px] bg-gradient-to-l from-[#FF6B35]/40 to-transparent rotate-45"></div>
      <div className="absolute top-[45%] right-[3%] w-24 h-[1px] bg-gradient-to-l from-[#FF6B35]/30 to-transparent rotate-12"></div>
      <div className="absolute bottom-[30%] left-[5%] w-20 h-[1px] bg-gradient-to-r from-[#1A1F36]/20 to-transparent -rotate-12"></div>
      
      <div className="max-w-[1400px] mx-auto px-6 py-20 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="max-w-[600px]">
            {/* Accent badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#FF6B35]/20 rounded-full text-sm mb-8 shadow-sm">
              <span className="w-2 h-2 bg-[#FF6B35] rounded-full animate-pulse"></span>
              <span className="text-[#1A1F36] font-medium">For UK Service Businesses</span>
            </div>
            
            <h1 className="text-5xl lg:text-[72px] font-bold text-[#1A1F36] leading-[1.05] mb-8 tracking-tight">
              Stop Chasing Leads.
              <br />
              <span className="relative inline-block">
                <span className="text-[#FF6B35]">Start Attracting</span>
                <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 6C50 2 150 2 198 6" stroke="#FF6B35" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.3"/>
                </svg>
              </span>
              <br />
              the Right Ones.
            </h1>
            
            <p className="text-xl lg:text-[22px] text-[#4A5568] leading-relaxed mb-10 max-w-[580px]">
              We analyse your online presence and show you <span className="text-[#1A1F36] font-medium">exactly how to attract higher-paying customers</span>—without the guesswork.
            </p>
            
            <div className="flex flex-col sm:flex-row items-start gap-4 mb-10">
              <button 
                onClick={onCTAClick}
                className="group px-10 py-4 bg-[#FF6B35] text-white text-lg font-semibold rounded-full hover:bg-[#E85A2A] hover:shadow-[0_8px_30px_rgba(255,107,53,0.35)] hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-3"
              >
                Get Your Free Digital Review
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
              </button>
              
              <div className="flex items-center gap-3 px-2 py-3">
                <div className="flex -space-x-2">
                  <div className="w-9 h-9 rounded-full bg-[#1A1F36] flex items-center justify-center text-white text-xs font-bold border-2 border-white">JT</div>
                  <div className="w-9 h-9 rounded-full bg-[#4A5568] flex items-center justify-center text-white text-xs font-bold border-2 border-white">MK</div>
                  <div className="w-9 h-9 rounded-full bg-[#FF6B35] flex items-center justify-center text-white text-xs font-bold border-2 border-white">SB</div>
                </div>
                <div className="text-sm text-[#4A5568]">
                  <span className="font-semibold text-[#1A1F36]">50+</span> businesses reviewed
                </div>
              </div>
            </div>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                <div className="w-5 h-5 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
                  <i className="ri-lock-line text-[#FF6B35] text-xs"></i>
                </div>
                <span>No login required</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                <div className="w-5 h-5 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
                  <i className="ri-time-line text-[#FF6B35] text-xs"></i>
                </div>
                <span>Results in 48 hours</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                <div className="w-5 h-5 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
                  <i className="ri-gift-line text-[#FF6B35] text-xs"></i>
                </div>
                <span>100% free</span>
              </div>
            </div>
          </div>

          {/* Right Visual - Dynamic Analytics Dashboard */}
          <div className="relative lg:block hidden">
            {/* Main Dashboard Card with 3D perspective */}
            <div className="relative" style={{ perspective: '1000px' }}>
              {/* Floating background cards */}
              <div className="absolute -top-8 -right-8 w-[400px] h-[280px] bg-gradient-to-br from-[#FF6B35]/20 to-[#FF6B35]/5 rounded-3xl blur-2xl animate-pulse"></div>
              <div className="absolute -bottom-8 -left-8 w-[350px] h-[250px] bg-gradient-to-tr from-[#1A1F36]/10 to-transparent rounded-3xl blur-2xl" style={{ animationDelay: '1s' }}></div>
              
              {/* Main Dashboard */}
              <div className="relative bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-8 border border-gray-100" style={{ transform: 'rotateY(-5deg) rotateX(2deg)' }}>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-[#1A1F36]">Digital Performance</h3>
                    <p className="text-sm text-[#4A5568]">Real-time insights</p>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#FF6B35]/10 rounded-full">
                    <span className="w-2 h-2 bg-[#FF6B35] rounded-full animate-pulse"></span>
                    <span className="text-xs font-semibold text-[#FF6B35]">Live</span>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-[#FF6B35]/5 to-transparent p-4 rounded-2xl border border-[#FF6B35]/10">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center">
                        <i className="ri-line-chart-line text-[#FF6B35]"></i>
                      </div>
                      <span className="text-xs font-medium text-[#4A5568]">Traffic</span>
                    </div>
                    <div className="text-2xl font-bold text-[#1A1F36]">+127%</div>
                    <div className="text-xs text-[#FF6B35] font-medium mt-1">↑ 2.4K visits</div>
                  </div>

                  <div className="bg-gradient-to-br from-[#1A1F36]/5 to-transparent p-4 rounded-2xl border border-[#1A1F36]/10">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-[#1A1F36]/10 flex items-center justify-center">
                        <i className="ri-user-add-line text-[#1A1F36]"></i>
                      </div>
                      <span className="text-xs font-medium text-[#4A5568]">Leads</span>
                    </div>
                    <div className="text-2xl font-bold text-[#1A1F36]">+89%</div>
                    <div className="text-xs text-[#FF6B35] font-medium mt-1">↑ 156 new</div>
                  </div>
                </div>

                {/* Chart Visualization */}
                <div className="relative h-[140px] bg-gradient-to-b from-[#FAFBFC] to-white rounded-2xl p-4 border border-gray-100">
                  <div className="flex items-end justify-between h-full gap-2">
                    {[40, 65, 45, 80, 60, 95, 75, 100, 85, 70, 90, 95].map((height, i) => (
                      <div key={i} className="flex-1 flex flex-col justify-end group cursor-pointer">
                        <div 
                          className="w-full bg-gradient-to-t from-[#FF6B35] to-[#FF6B35]/60 rounded-t-lg transition-all duration-300 group-hover:from-[#FF6B35] group-hover:to-[#FF6B35]/80"
                          style={{ 
                            height: `${height}%`,
                            animation: `slideUp 0.6s ease-out ${i * 0.05}s backwards`
                          }}
                        ></div>
                      </div>
                    ))}
                  </div>
                  <div className="absolute top-2 right-2 text-xs font-semibold text-[#4A5568]">Last 12 months</div>
                </div>

                {/* Bottom Metrics */}
                <div className="grid grid-cols-3 gap-3 mt-6">
                  <div className="text-center p-3 bg-[#FAFBFC] rounded-xl">
                    <div className="text-lg font-bold text-[#1A1F36]">4.8</div>
                    <div className="text-xs text-[#4A5568] mt-1">Rating</div>
                  </div>
                  <div className="text-center p-3 bg-[#FAFBFC] rounded-xl">
                    <div className="text-lg font-bold text-[#1A1F36]">92%</div>
                    <div className="text-xs text-[#4A5568] mt-1">Quality</div>
                  </div>
                  <div className="text-center p-3 bg-[#FAFBFC] rounded-xl">
                    <div className="text-lg font-bold text-[#1A1F36]">£2.4K</div>
                    <div className="text-xs text-[#4A5568] mt-1">Avg Value</div>
                  </div>
                </div>
              </div>

              {/* Floating Metric Cards */}
              <div className="absolute -left-12 top-1/4 bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] p-4 border border-gray-100 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#E85A2A] flex items-center justify-center">
                    <i className="ri-search-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-[#4A5568]">Google Rank</div>
                    <div className="text-lg font-bold text-[#1A1F36]">#3 → #1</div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-8 bottom-1/4 bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] p-4 border border-gray-100 animate-float" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1A1F36] to-[#2A3150] flex items-center justify-center">
                    <i className="ri-star-line text-[#FF6B35] text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-[#4A5568]">Reviews</div>
                    <div className="text-lg font-bold text-[#1A1F36]">4.9★</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom accent bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FF6B35]/30 to-transparent"></div>

      <style>{`
        @keyframes slideUp {
          from {
            height: 0;
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
